import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import {
  batchCreateSortingDecisions,
  bulkRouteAssign,
  bulkSortingWarehouse,
  confirmSortingDecision,
  createSortingDecision,
  fetchSortingDecisions,
  fetchSortingPackages,
  type SortingListMeta,
} from './api';
import type {
  BulkRouteInput,
  BulkSortingWarehouseInput,
  SortingDecisionDto,
  SortingDecisionCreateInput,
  SortingPackageDto,
  SortingPackageFilters,
} from './types';

const KEY = 'sorting-decisions' as const;
const PKG_KEY = 'sorting-packages' as const;

// ---------------------------------------------------------------------------
// New per-package hooks.
// ---------------------------------------------------------------------------

export const useSortingPackages = (filters: SortingPackageFilters = {}) => {
  return useQuery<{ data: SortingPackageDto[]; meta: SortingListMeta }, ApiClientError>({
    queryKey: [PKG_KEY, 'list', filters],
    queryFn: () => fetchSortingPackages(filters),
    placeholderData: (prev) => prev,
  });
};

export const useBulkRouteAssign = () => {
  const qc = useQueryClient();
  return useMutation<{ updated: number }, ApiClientError, BulkRouteInput>({
    mutationFn: (body) => bulkRouteAssign(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PKG_KEY] }),
  });
};

export const useBulkSortingWarehouse = () => {
  const qc = useQueryClient();
  return useMutation<{ updated: number }, ApiClientError, BulkSortingWarehouseInput>({
    mutationFn: (body) => bulkSortingWarehouse(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [PKG_KEY] }),
  });
};

// ---------------------------------------------------------------------------
// Legacy hooks retained for back-compat with the older sorting-decision UI.
// ---------------------------------------------------------------------------

export const useSortingDecisionsList = (
  batchId: string | undefined,
  params: { page?: number; per_page?: number } = {},
) => {
  return useQuery<{ data: SortingDecisionDto[]; meta: SortingListMeta }, ApiClientError>({
    queryKey: [KEY, 'list', batchId, params],
    queryFn: () => fetchSortingDecisions(batchId!, params),
    enabled: !!batchId,
    placeholderData: (prev) => prev,
  });
};

export const useCreateSortingDecision = () => {
  const qc = useQueryClient();
  return useMutation<
    SortingDecisionDto,
    ApiClientError,
    { lotId: string; body: SortingDecisionCreateInput }
  >({
    mutationFn: ({ lotId, body }) => createSortingDecision(lotId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useConfirmSortingDecision = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => confirmSortingDecision(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useBatchCreateSortingDecisions = () => {
  const qc = useQueryClient();
  return useMutation<
    void,
    ApiClientError,
    { batchId: string; route_type: SortingDecisionDto['route_type']; decision_reason?: string }
  >({
    mutationFn: ({ batchId, route_type, decision_reason }) =>
      batchCreateSortingDecisions(batchId, { route_type, decision_reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};
